(() => {
  const runtimeKey = '__yourStudioStory';
  if (window[runtimeKey]) {
    window[runtimeKey].init(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const anchorGap = 16;
  const videoBufferCheckInterval = 250;
  const stickyHeaderSelector = '.shopify-section--announcement-bar, .shopify-section--header';

  const getStickyHeaderBottom = () => Array.from(document.querySelectorAll(stickyHeaderSelector)).reduce((bottom, element) => {
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    const isStickyLayer = style.position === 'sticky' || style.position === 'fixed';
    const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && rect.bottom > 0 && rect.top < window.innerHeight;

    return isStickyLayer && isVisible ? Math.max(bottom, rect.bottom) : bottom;
  }, 0);

  const initCarousel = (root) => {
    const rail = root.querySelector('[data-carousel-rail]');
    const previous = root.querySelector('[data-carousel-previous]');
    const next = root.querySelector('[data-carousel-next]');

    if (!rail || !previous || !next || rail.dataset.yourStudioStoryInitialized === 'true') return;

    rail.dataset.yourStudioStoryInitialized = 'true';
    const controller = new AbortController();
    rail.yourStudioStoryController = controller;

    const updateControls = () => {
      const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
      previous.disabled = rail.scrollLeft <= 2;
      next.disabled = rail.scrollLeft >= maxScroll - 2;
    };

    const move = (direction) => {
      rail.scrollBy({
        left: direction * Math.max(260, rail.clientWidth * 0.78),
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    };

    previous.addEventListener('click', () => move(-1), { signal: controller.signal });
    next.addEventListener('click', () => move(1), { signal: controller.signal });
    rail.addEventListener('scroll', updateControls, { passive: true, signal: controller.signal });
    window.addEventListener('resize', updateControls, { passive: true, signal: controller.signal });
    updateControls();
  };

  const initHeroVideo = (root) => {
    const lowVideo = root.querySelector('[data-your-studio-story-video-low]');
    const highVideo = root.querySelector('[data-your-studio-story-video-high]');
    const loader = root.querySelector('[data-your-studio-story-video-loader]');

    if (!lowVideo || lowVideo.dataset.yourStudioStoryInitialized === 'true') return;

    const videos = [lowVideo, highVideo].filter(Boolean);
    const controller = new AbortController();
    let lowBufferMonitor = null;
    let highBufferMonitor = null;
    let highSeekFallback = null;
    let lowPlaybackRequested = false;
    let highPlaybackRequested = false;
    let highLoadStarted = false;

    videos.forEach((video) => {
      video.dataset.yourStudioStoryInitialized = 'true';
      video.yourStudioStoryController = controller;
    });

    const setVideoState = (state) => {
      root.dataset.yourStudioStoryVideoState = state;
      loader?.setAttribute('aria-hidden', state === 'loading' ? 'false' : 'true');
    };

    const clearLowBufferMonitor = () => {
      if (lowBufferMonitor === null) return;

      window.clearInterval(lowBufferMonitor);
      lowBufferMonitor = null;
    };

    const clearHighBufferMonitor = () => {
      if (highBufferMonitor === null) return;

      window.clearInterval(highBufferMonitor);
      highBufferMonitor = null;
    };

    const clearHighSeekFallback = () => {
      if (highSeekFallback === null) return;

      window.clearTimeout(highSeekFallback);
      highSeekFallback = null;
    };

    const getContiguousBufferEnd = (video) => {
      let contiguousEnd = 0;

      for (let index = 0; index < video.buffered.length; index += 1) {
        const rangeStart = video.buffered.start(index);
        const rangeEnd = video.buffered.end(index);

        if (rangeStart > contiguousEnd + 0.15) break;
        contiguousEnd = Math.max(contiguousEnd, rangeEnd);
      }

      return contiguousEnd;
    };

    const isFullyBuffered = (video) => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) return false;

      const endTolerance = Math.min(0.1, video.duration * 0.01);
      return getContiguousBufferEnd(video) >= video.duration - endTolerance;
    };

    const prepareVideo = (video) => {
      video.preload = 'auto';
      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
    };

    const pauseVideos = () => {
      videos.forEach((video) => video.pause());
    };

    const requestHighPlayback = () => {
      if (!highVideo || reducedMotion.matches || !isFullyBuffered(highVideo)) return;
      if (highPlaybackRequested || !highVideo.paused) return;

      highPlaybackRequested = true;
      clearHighBufferMonitor();
      const highDuration = highVideo.duration;
      const syncTime = Number.isFinite(highDuration) && highDuration > 0
        ? lowVideo.currentTime % highDuration
        : 0;
      let playbackStarted = false;

      const startPlayback = () => {
        if (playbackStarted) return;

        playbackStarted = true;
        clearHighSeekFallback();
        highVideo.play().catch(() => {
          highPlaybackRequested = false;
          setVideoState('low-playing');
        });
      };

      if (Math.abs(highVideo.currentTime - syncTime) > 0.04) {
        highVideo.addEventListener('seeked', startPlayback, { once: true, signal: controller.signal });
        highVideo.currentTime = syncTime;
        highSeekFallback = window.setTimeout(startPlayback, 180);
      } else {
        startPlayback();
      }
    };

    const startHighBufferMonitor = () => {
      if (!highVideo || highBufferMonitor !== null || reducedMotion.matches) return;

      highBufferMonitor = window.setInterval(requestHighPlayback, videoBufferCheckInterval);
    };

    const waitForHighBuffer = () => {
      if (!highVideo || reducedMotion.matches) return;

      startHighBufferMonitor();
      requestHighPlayback();
    };

    const startHighLoad = () => {
      if (!highVideo || highLoadStarted || reducedMotion.matches) return;

      highLoadStarted = true;
      prepareVideo(highVideo);
      waitForHighBuffer();
    };

    const requestLowPlayback = () => {
      if (reducedMotion.matches) {
        lowPlaybackRequested = false;
        highPlaybackRequested = false;
        clearLowBufferMonitor();
        clearHighBufferMonitor();
        clearHighSeekFallback();
        pauseVideos();
        setVideoState('paused');
        return;
      }

      if (!isFullyBuffered(lowVideo)) {
        setVideoState('loading');
        return;
      }

      if (lowPlaybackRequested || !lowVideo.paused) return;

      lowPlaybackRequested = true;
      clearLowBufferMonitor();
      if (lowVideo.currentTime > 0.05) lowVideo.currentTime = 0;
      lowVideo.play().catch(() => {
        lowPlaybackRequested = false;
        if (highVideo) {
          setVideoState('loading');
          startHighLoad();
        } else {
          setVideoState('error');
        }
      });
    };

    const startLowBufferMonitor = () => {
      if (lowBufferMonitor !== null || reducedMotion.matches) return;

      lowBufferMonitor = window.setInterval(requestLowPlayback, videoBufferCheckInterval);
    };

    const waitForLowBuffer = () => {
      if (reducedMotion.matches) return;

      setVideoState('loading');
      startLowBufferMonitor();
      requestLowPlayback();
    };

    const handleLowPlaybackInterruption = () => {
      if (reducedMotion.matches) return;

      lowPlaybackRequested = false;
      lowVideo.pause();
      waitForLowBuffer();
    };

    const handleHighPlaybackInterruption = () => {
      if (reducedMotion.matches) return;

      highPlaybackRequested = false;
      highVideo.pause();
      setVideoState('low-playing');
    };

    const handleMotionPreference = () => {
      if (reducedMotion.matches) {
        requestLowPlayback();
        return;
      }

      prepareVideo(lowVideo);
      waitForLowBuffer();
    };

    ['loadstart', 'loadedmetadata', 'loadeddata', 'progress', 'canplaythrough', 'suspend'].forEach((eventName) => {
      lowVideo.addEventListener(eventName, waitForLowBuffer, { signal: controller.signal });
      highVideo?.addEventListener(eventName, waitForHighBuffer, { signal: controller.signal });
    });
    lowVideo.addEventListener('waiting', handleLowPlaybackInterruption, { signal: controller.signal });
    lowVideo.addEventListener('stalled', waitForLowBuffer, { signal: controller.signal });
    lowVideo.addEventListener('playing', () => {
      setVideoState('low-playing');
      startHighLoad();
    }, { signal: controller.signal });
    lowVideo.addEventListener('error', () => {
      lowPlaybackRequested = false;
      clearLowBufferMonitor();
      if (highVideo) {
        setVideoState('loading');
        startHighLoad();
      } else {
        setVideoState('error');
      }
    }, { signal: controller.signal });
    highVideo?.addEventListener('waiting', handleHighPlaybackInterruption, { signal: controller.signal });
    highVideo?.addEventListener('stalled', waitForHighBuffer, { signal: controller.signal });
    highVideo?.addEventListener('playing', () => setVideoState('high-playing'), { signal: controller.signal });
    highVideo?.addEventListener('error', () => {
      highPlaybackRequested = false;
      clearHighBufferMonitor();
      setVideoState(lowVideo.paused ? 'error' : 'low-playing');
    }, { signal: controller.signal });
    reducedMotion.addEventListener('change', handleMotionPreference, { signal: controller.signal });
    controller.signal.addEventListener('abort', () => {
      clearLowBufferMonitor();
      clearHighBufferMonitor();
      clearHighSeekFallback();
    }, { once: true });
    handleMotionPreference();
  };

  const initAnchorLink = (link) => {
    if (link.dataset.yourStudioStoryInitialized === 'true') return;

    const targetSelector = link.getAttribute('href');
    if (!targetSelector?.startsWith('#')) return;

    link.dataset.yourStudioStoryInitialized = 'true';
    const controller = new AbortController();
    link.yourStudioStoryController = controller;

    link.addEventListener('click', (event) => {
      const target = document.querySelector(targetSelector);
      if (!target) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const headerBottom = getStickyHeaderBottom();
      const targetTop = Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerBottom - anchorGap);

      window.scrollTo({
        top: targetTop,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
      window.history.pushState(null, '', targetSelector);
    }, { capture: true, signal: controller.signal });
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-your-studio-story-carousel]').forEach(initCarousel);
    scope.querySelectorAll('[data-your-studio-story-root]').forEach(initHeroVideo);
    scope.querySelectorAll('[data-your-studio-story-anchor]').forEach(initAnchorLink);
  };

  window[runtimeKey] = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-carousel-rail], .your-studio-story-hero__video, [data-your-studio-story-anchor]').forEach((element) => {
      element.yourStudioStoryController?.abort();
      delete element.yourStudioStoryController;
      delete element.dataset.yourStudioStoryInitialized;
    });
  });
})();
